import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { Checklist, ChecklistItem } from '../types';

// Collection references
const checklistsCollection = collection(db, 'checklists');
const checklistItemsCollection = collection(db, 'checklistItems');

// Get all checklists for a project
export const getChecklistsByProject = async (projectId: string): Promise<Checklist[]> => {
  try {
    const q = query(
      checklistsCollection,
      where('projectId', '==', projectId)
    );
    
    const querySnapshot = await getDocs(q);
    const checklists: Checklist[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      checklists.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      } as Checklist);
    });
    
    return checklists;
  } catch (error) {
    console.error('Error getting checklists:', error);
    throw error;
  }
};

// Get a single checklist by ID
export const getChecklist = async (checklistId: string): Promise<Checklist> => {
  try {
    const checklistDoc = await getDoc(doc(checklistsCollection, checklistId));
    
    if (!checklistDoc.exists()) {
      throw new Error(`Checklist with ID ${checklistId} not found`);
    }
    
    return { id: checklistDoc.id, ...checklistDoc.data() } as Checklist;
  } catch (error) {
    console.error('Error getting checklist:', error);
    throw error;
  }
};

// Create a new checklist
export const createChecklist = async (
  projectId: string,
  type: string,
  name?: string,
  description?: string
): Promise<Checklist> => {
  try {
    const checklistData: Omit<Checklist, 'id'> = {
      projectId,
      type,
      name: name || '',
      description: description || '',
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp
    };
    
    const docRef = await addDoc(checklistsCollection, checklistData);
    
    return {
      id: docRef.id,
      ...checklistData,
      createdAt: checklistData.createdAt,
      updatedAt: checklistData.updatedAt
    };
  } catch (error) {
    console.error('Error creating checklist:', error);
    throw error;
  }
};

// Update a checklist
export const updateChecklist = async (
  checklistId: string,
  data: Partial<Omit<Checklist, 'id' | 'projectId' | 'createdAt'>>
): Promise<void> => {
  try {
    await updateDoc(doc(checklistsCollection, checklistId), {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating checklist:', error);
    throw error;
  }
};

// Delete a checklist
export const deleteChecklist = async (checklistId: string): Promise<void> => {
  try {
    // First, delete all checklist items
    const q = query(
      checklistItemsCollection,
      where('checklistId', '==', checklistId)
    );
    
    const querySnapshot = await getDocs(q);
    
    const deletePromises = querySnapshot.docs.map((docSnapshot) => 
      deleteDoc(doc(checklistItemsCollection, docSnapshot.id))
    );
    
    await Promise.all(deletePromises);
    
    // Then delete the checklist
    await deleteDoc(doc(checklistsCollection, checklistId));
  } catch (error) {
    console.error('Error deleting checklist:', error);
    throw error;
  }
};

// Get checklist items by checklist ID
export const getChecklistItems = async (checklistId: string): Promise<ChecklistItem[]> => {
  try {
    const q = query(
      checklistItemsCollection,
      where('checklistId', '==', checklistId),
      orderBy('order', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const items: ChecklistItem[] = [];
    
    querySnapshot.forEach((doc) => {
      items.push({ id: doc.id, ...doc.data() } as ChecklistItem);
    });
    
    return items;
  } catch (error) {
    console.error('Error getting checklist items:', error);
    throw error;
  }
};

// Create a new checklist item
export const createChecklistItem = async (
  checklistId: string,
  text: string,
  order?: number
): Promise<ChecklistItem> => {
  try {
    // Get the current highest order if not provided
    let itemOrder = order ?? 0; // Default to 0 if undefined
    
    if (order === undefined) {
      const q = query(
        checklistItemsCollection,
        where('checklistId', '==', checklistId),
        orderBy('order', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.docs.length > 0) {
        const highestOrder = querySnapshot.docs[0].data().order || 0;
        itemOrder = highestOrder + 1;
      } else {
        itemOrder = 0;
      }
    }
    
    const itemData: Omit<ChecklistItem, 'id'> = {
      checklistId,
      text,
      completed: false,
      order: itemOrder,
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp
    };
    
    const docRef = await addDoc(checklistItemsCollection, itemData);
    
    return {
      id: docRef.id,
      ...itemData,
      createdAt: itemData.createdAt,
      updatedAt: itemData.updatedAt
    };
  } catch (error) {
    console.error('Error creating checklist item:', error);
    throw error;
  }
};

// Update a checklist item
export const updateChecklistItem = async (
  itemId: string,
  data: Partial<Omit<ChecklistItem, 'id' | 'checklistId' | 'createdAt'>>
): Promise<void> => {
  try {
    await updateDoc(doc(checklistItemsCollection, itemId), {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating checklist item:', error);
    throw error;
  }
};

// Delete a checklist item
export const deleteChecklistItem = async (itemId: string): Promise<void> => {
  try {
    await deleteDoc(doc(checklistItemsCollection, itemId));
  } catch (error) {
    console.error('Error deleting checklist item:', error);
    throw error;
  }
};

// Toggle a checklist item's completed status
export const toggleChecklistItem = async (
  itemId: string,
  completed: boolean,
  completedBy?: string
): Promise<void> => {
  try {
    // Create the base update data
    const updateData: any = {
      completed,
      updatedAt: serverTimestamp() as Timestamp
    };
    
    // Add completion data if completed
    if (completed) {
      updateData.completedAt = serverTimestamp() as Timestamp;
      if (completedBy) {
        updateData.completedBy = completedBy;
      }
    } else {
      updateData.completedAt = null;
      updateData.completedBy = null;
    }
    
    await updateDoc(doc(checklistItemsCollection, itemId), updateData);
  } catch (error) {
    console.error('Error toggling checklist item:', error);
    throw error;
  }
};

// Reorder checklist items
export const reorderChecklistItems = async (
  items: { id: string; order: number }[]
): Promise<void> => {
  try {
    const batch = db.batch ? db.batch() : null;
    
    if (!batch) {
      // Fall back to individual updates if batch is not available
      const updatePromises = items.map(item => 
        updateDoc(doc(checklistItemsCollection, item.id), { 
          order: item.order,
          updatedAt: serverTimestamp()
        })
      );
      
      await Promise.all(updatePromises);
    } else {
      items.forEach((item) => {
        const itemRef = doc(checklistItemsCollection, item.id);
        batch.update(itemRef, { 
          order: item.order,
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
    }
  } catch (error) {
    console.error('Error reordering checklist items:', error);
    throw error;
  }
}; 